import { parseMetaClickId } from "@/utils/tracking/metaClickId";

export const FIRST_TOUCH_ATTRIBUTION_COOKIE = "gg_attr_first";
export const LAST_TOUCH_ATTRIBUTION_COOKIE = "gg_attr_last";
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
// Set only with CookieYes analytics consent (proxy.ts, AttributionOnConsent).
export const ATTRIBUTION_COOKIE_OPTIONS = {
  httpOnly: true,
  maxAge: ATTRIBUTION_COOKIE_MAX_AGE,
  path: "/",
  sameSite: "lax" as const,
  secure: true,
};

// Google Ads click identifiers besides gclid: gbraid/wbraid (iOS app/web
// measurement), gad_campaignid and gad_source (auto-tagging context).
const GOOGLE_CLICK_KEYS = [
  "gbraid",
  "wbraid",
  "gad_campaignid",
  "gad_source",
] as const;
const ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
  "li_fat_id",
  ...GOOGLE_CLICK_KEYS,
] as const;
// Keys stored one-per-field in Stripe metadata. The Google click keys above
// are packed into one `attribution_<touch>_google` value instead, to stay
// below Stripe's 50 metadata keys per checkout.
const ATTRIBUTION_TOUCH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
  "li_fat_id",
  "landing_path",
  "referrer",
] as const;
const ATTRIBUTION_DETAIL_KEYS = ["utm_id", "ad_id", "adset_id", "captured_at"] as const;
// URL-safe ids with per-key length caps, so the packed JSON stays under
// Stripe's 500-character metadata value limit (at most ~400 characters).
const PACKED_GOOGLE_VALUE_PATTERNS: Record<(typeof GOOGLE_CLICK_KEYS)[number], RegExp> = {
  gbraid: /^[a-zA-Z0-9_-]{1,150}$/,
  wbraid: /^[a-zA-Z0-9_-]{1,150}$/,
  gad_campaignid: /^[0-9]{1,30}$/,
  gad_source: /^[a-zA-Z0-9_-]{1,10}$/,
};

type AttributionQueryKey = (typeof ATTRIBUTION_QUERY_KEYS)[number];

export type AttributionTouch = Partial<
  Record<AttributionQueryKey, string>
> & {
  landing_path: string;
  referrer?: string;
} & Partial<Record<(typeof ATTRIBUTION_DETAIL_KEYS)[number], string>>;

type CookieReader = {
  get(name: string): { value: string } | undefined;
};

function truncate(value: string, maxLength: number): string {
  return value.trim().slice(0, maxLength);
}

function getReferrerHost(referrer: string | null): string | undefined {
  if (!referrer) {
    return undefined;
  }

  try {
    return truncate(new URL(referrer).host, 120) || undefined;
  } catch {
    return undefined;
  }
}

export function buildAttributionTouch(
  url: URL,
  referrer: string | null
): AttributionTouch | null {
  const touch: Partial<AttributionTouch> = {};

  for (const key of ATTRIBUTION_QUERY_KEYS) {
    const value = url.searchParams.get(key);
    // An fbclid that isn't a Meta click ID (say, one with Parameter Builder's
    // appendix) records no click.
    if (value && (key !== "fbclid" || parseMetaClickId(value))) {
      touch[key] = truncate(value, 150);
    }
  }

  for (const key of ATTRIBUTION_DETAIL_KEYS) {
    if (key === "captured_at") continue;
    const value = url.searchParams.get(key);
    if (value && /^[a-zA-Z0-9_-]+$/.test(value)) touch[key] = truncate(value, 100);
  }

  const hasAttribution = [...ATTRIBUTION_QUERY_KEYS, "utm_id", "ad_id", "adset_id"].some(
    (key) => touch[key as keyof AttributionTouch]
  );
  if (!hasAttribution) {
    return null;
  }

  touch.landing_path = truncate(url.pathname, 300);
  touch.referrer = getReferrerHost(referrer);
  touch.captured_at = new Date().toISOString();

  return touch as AttributionTouch;
}

export function serializeAttributionTouch(touch: AttributionTouch): string {
  return JSON.stringify(touch);
}

export function parseAttributionTouch(
  value: string | null | undefined
): AttributionTouch | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) ||
        typeof parsed.landing_path !== "string" || !parsed.landing_path.startsWith("/")) {
      return null;
    }
    const touch: AttributionTouch = { landing_path: truncate(parsed.landing_path, 300) };
    for (const key of [...ATTRIBUTION_QUERY_KEYS, "referrer", ...ATTRIBUTION_DETAIL_KEYS] as const) {
      if (parsed[key] === undefined) continue;
      if (typeof parsed[key] !== "string") return null;
      if (key === "captured_at") {
        if (Number.isFinite(Date.parse(parsed[key]))) touch[key] = new Date(parsed[key]).toISOString();
      } else if (key === "utm_id" || key === "ad_id" || key === "adset_id") {
        if (/^[a-zA-Z0-9_-]+$/.test(parsed[key])) touch[key] = truncate(parsed[key], 100);
      } else {
        touch[key] = truncate(parsed[key], key === "referrer" ? 120 : 150);
      }
    }
    return touch;
  } catch {
    return null;
  }
}

export function readCheckoutAttribution(cookieStore: CookieReader): {
  firstTouch: AttributionTouch | null;
  lastTouch: AttributionTouch | null;
} {
  return {
    firstTouch: parseAttributionTouch(
      cookieStore.get(FIRST_TOUCH_ATTRIBUTION_COOKIE)?.value
    ),
    lastTouch: parseAttributionTouch(
      cookieStore.get(LAST_TOUCH_ATTRIBUTION_COOKIE)?.value
    ),
  };
}

export function getStripeAttributionMetadata(
  firstTouch: AttributionTouch | null,
  lastTouch: AttributionTouch | null
): Record<string, string> {
  const metadata: Record<string, string> = {};

  const addTouch = (
    prefix: "first" | "last",
    touch: AttributionTouch | null
  ) => {
    if (!touch) {
      return;
    }

    for (const key of ATTRIBUTION_TOUCH_KEYS) {
      const value = touch[key];
      if (value) {
        metadata[`attribution_${prefix}_${key}`] = value;
      }
    }
    // Keep legacy fields readable for existing consumers. Pack additional IDs
    // and time into one bounded value per touch to stay below Stripe's 50 keys.
    const details = Object.fromEntries(ATTRIBUTION_DETAIL_KEYS
      .filter((key) => touch[key])
      .map((key) => [key, touch[key]]));
    if (Object.keys(details).length) {
      metadata[`attribution_${prefix}_details`] = JSON.stringify(details);
    }
    const google = Object.fromEntries(GOOGLE_CLICK_KEYS
      .filter((key) => PACKED_GOOGLE_VALUE_PATTERNS[key].test(touch[key] ?? ""))
      .map((key) => [key, touch[key]]));
    if (Object.keys(google).length) {
      metadata[`attribution_${prefix}_google`] = JSON.stringify(google);
    }
  };

  addTouch("first", firstTouch);
  addTouch("last", lastTouch);

  return metadata;
}

export function getAttributionPropertiesFromStripeMetadata(
  metadata: Record<string, string> | null | undefined
): Record<string, string> {
  if (!metadata) {
    return {};
  }

  const properties: Record<string, string> = {};
  for (const prefix of ["first", "last"] as const) {
    for (const key of ATTRIBUTION_TOUCH_KEYS) {
      const name = `attribution_${prefix}_${key}`;
      if (typeof metadata[name] === "string" && metadata[name]) properties[name] = metadata[name];
    }
    try {
      const details = JSON.parse(metadata[`attribution_${prefix}_details`] || "{}");
      for (const key of ATTRIBUTION_DETAIL_KEYS) {
        if (typeof details?.[key] === "string" && details[key]) {
          properties[`attribution_${prefix}_${key}`] = details[key];
        }
      }
    } catch { /* Old checkouts have no details; malformed data is ignored. */ }
    try {
      const google = JSON.parse(metadata[`attribution_${prefix}_google`] || "{}");
      for (const key of GOOGLE_CLICK_KEYS) {
        if (typeof google?.[key] === "string" && google[key]) {
          properties[`attribution_${prefix}_${key}`] = google[key];
        }
      }
    } catch { /* Older checkouts have no Google click ids. */ }
  }
  // Choose a whole visit, never fall back field-by-field to an earlier campaign.
  const prefix = Object.keys(properties).some((key) => key.startsWith("attribution_last_")) ? "last" : "first";
  for (const key of [...ATTRIBUTION_QUERY_KEYS, ...ATTRIBUTION_DETAIL_KEYS]) {
    const value = properties[`attribution_${prefix}_${key}`];
    if (value) {
      properties[key] = value;
    }
  }

  return properties;
}
